import {Grid, Stack} from '@mui/material';
import {AppButton} from 'components/buttons';
import {AppDialog} from 'components/dialogs';
import {AppControlledDateTimePicker, AppControlledInput} from 'components/form';
import {AppControlledEditor} from 'components/form/AppTextEditor';
import React, {useEffect} from 'react';

import {useSessionRedux} from 'hooks';

import {useCreateReminder, useUpdateReminder} from 'hooks/reminders/useReminderApi';
import {
    REMINDER_OBJECT_TYPE,
    REMINDER_TYPE,
    ReminderCreateUpdateRequestModel,
    reminderDefaultValues,
    ReminderModel,
} from 'models/reminder';
import {useForm} from 'react-hook-form';
import {useSelector} from 'react-redux';
import {sessionSelector} from 'store/auth/sessionSlice';

interface AddUpdateReminderDialogProps {
    open: boolean;
    onCloseDialog: () => void;
    ticketNoValue?: number;
    objectId?: string;
    objectType?: REMINDER_OBJECT_TYPE;
    type?: REMINDER_TYPE;
    onSuccess: (reminderResponse?: ReminderModel) => void;
    reminder: ReminderModel | null;
}

export const AddUpdateReminderDialog: React.FC<AddUpdateReminderDialogProps> = ({
    open,
    onCloseDialog,
    onSuccess,
    reminder,
    objectId,
    objectType,
    ticketNoValue,
    type = REMINDER_TYPE.MANUAL,
}) => {
    const {userId} = useSelector(sessionSelector);
    const {
        setValue,
        control,
        handleSubmit,
        formState: {isValid},
    } = useForm<ReminderCreateUpdateRequestModel>({
        defaultValues: {
            ...reminderDefaultValues,
            reminderDate: new Date(new Date().getTime() + 15 * 60000),
            userId: userId,
            objectId: objectId,
            objectType: objectType,
            type: type,
        },
    });

    const {createReminder, createReminderLoading, createReminderResponse} = useCreateReminder();
    const {updateReminder, updateReminderLoading, updateReminderResponse} = useUpdateReminder();

    const subHandler = (value: ReminderCreateUpdateRequestModel) => {
        if (reminder && reminder.id) {
            updateReminder({...value, id: reminder.id, reminderDate: value.reminderDate.toISOString()});
        } else {
            createReminder({...value, reminderDate: value.reminderDate.toISOString()});
        }
    };

    useEffect(() => {
        if (reminder && reminder.id) {
            setValue('description', reminder.description);
            setValue('title', reminder.title);
            setValue('reminderDate', new Date(reminder.reminderDate));
            setValue('type', reminder.type);
            setValue('status', reminder.status);
            setValue('userId', reminder.userId);
            setValue('objectId', reminder.objectId);
            setValue('objectType', reminder.objectType);
        }
    }, [reminder]);

    useEffect(() => {
        if (createReminderResponse) {
            onSuccess(createReminderResponse as ReminderModel);
            onCloseDialog();
        } else if (updateReminderResponse) {
            onSuccess(updateReminderResponse as ReminderModel);
            onCloseDialog();
        }
    }, [createReminderResponse, updateReminderResponse]);

    return (
        <AppDialog
            title={`${reminder && reminder.id ? 'Update' : 'Add'} Reminder`}
            fullWidth
            open={open}
            sx={{overflowX: 'hidden'}}
            onCloseDialog={() => {
                onCloseDialog();
            }}
        >
            <form>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <Stack spacing={1}>
                            <AppControlledInput
                                label={'Title'}
                                placeholder="Enter Reminder Title"
                                name="title"
                                control={control}
                                required={'Title is required'}
                            />
                        </Stack>
                    </Grid>
                    <Grid item xs={12}>
                        <Stack spacing={1}>
                            <AppControlledDateTimePicker
                                control={control}
                                minDate={new Date()}
                                minTime={new Date()}
                                required={'Date is required'}
                                label={'Date'}
                                name="reminderDate"
                            />
                        </Stack>
                    </Grid>
                    <Grid item xs={12}>
                        <Stack spacing={1}>
                            <AppControlledEditor
                                control={control}
                                name="description"
                                label={'Description'}
                                key={'text-quill-editor'}
                            />
                        </Stack>
                    </Grid>
                    <Grid item xs={12}>
                        <Stack direction="row" spacing={2} justifyContent="flex-end">
                            <AppButton
                                color="error"
                                variant="outlined"
                                onClick={() => {
                                    onCloseDialog();
                                }}
                            >
                                Cancel
                            </AppButton>
                            <AppButton
                                isLoading={createReminderLoading || updateReminderLoading}
                                isAnimated={isValid}
                                onClick={handleSubmit(subHandler)}
                                variant="contained"
                            >
                                {reminder && reminder.id ? 'Update' : 'Add'}
                            </AppButton>
                        </Stack>
                    </Grid>
                </Grid>
            </form>
        </AppDialog>
    );
};

export default AddUpdateReminderDialog;
